import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import moment from 'moment';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const formatDate = date => (date ? moment(date).format('MM/DD/YY hh:mm A') : '-');

const CycleAttestation = ({ attestations, loading = false }) => {
    const [selectedPatientId, setSelectedPatientId] = useState('');
    const [selectedSource, setSelectedSource] = useState('');
    const [activeFilter, setActiveFilter] = useState('ALL');

    const rawRows = useMemo(() => {
        if (!attestations) return [];
        if (Array.isArray(attestations)) {
            return [...attestations].sort((a, b) => {
                const isANew = (a.source || '').toLowerCase() === 'interim';
                const isBNew = (b.source || '').toLowerCase() === 'interim';
                if (isANew && !isBNew) return -1;
                if (!isANew && isBNew) return 1;
                return 0;
            });
        }
        const cycleRows = Array.isArray(attestations.cycle_rows) ? attestations.cycle_rows : [];
        const interimRows = Array.isArray(attestations.interim_rows) ? attestations.interim_rows : [];
        return [...interimRows, ...cycleRows];
    }, [attestations]);

    const patientMap = useMemo(() => {
        const map = new Map();
        rawRows.forEach(item => {
            const pId = item.patient_id;
            if (!pId) return;
            if (!map.has(pId)) {
                const pName =
                    item.patient_name ||
                    (item.patient_firstname
                        ? `${item.patient_firstname} ${item.patient_lastname || ''}`.trim()
                        : '')
                    // `Patient #${pId}`;
                map.set(pId, {
                    patient_id: pId,
                    // external_patient_id: item.external_patient_id || pId,
                    patient_name: pName,
                    prescriptions: []
                });
            }
            map.get(pId).prescriptions.push(item);
        });
        return map;
    }, [rawRows]);

    const isPatientFullyAttested = useMemo(() => {
        return pId => {
            const patient = patientMap.get(pId);
            if (!patient || patient.prescriptions.length === 0) return false;
            return patient.prescriptions.every(
                rx => Number(rx.rx_attested) === 1 || rx.rx_attested === true
            );
        };
    }, [patientMap]);

    const patients = useMemo(() => {
        return Array.from(patientMap.values());
    }, [patientMap]);

    const summary = useMemo(() => {
        const totalPatients = patients.length;
        let fullyAttestedPatients = 0;
        patients.forEach(p => {
            if (isPatientFullyAttested(p.patient_id)) {
                fullyAttestedPatients++;
            }
        });
        const pendingPatients = totalPatients - fullyAttestedPatients;
        const totalPrescriptions = rawRows.length;
        const pendingRxAttestation = rawRows.filter(
            r => !(Number(r.rx_attested) === 1 || r.rx_attested === true)
        ).length;
        const pendingHcpConfirmation = rawRows.filter(
            r => !(Number(r.hcp_confirmed) === 1 || r.hcp_confirmed === true)
        ).length;

        return {
            total_patients: totalPatients,
            fully_attested_patients: fullyAttestedPatients,
            pending_patients: pendingPatients,
            total_prescriptions: totalPrescriptions,
            pending_rx_attestation: pendingRxAttestation,
            pending_hcp_confirmation: pendingHcpConfirmation
        };
    }, [patients, isPatientFullyAttested, rawRows]);

    const rows = useMemo(() => {
        let resultRows = rawRows.map(item => {
            const pId = item.patient_id;
            const patientInfo = patientMap.get(pId);
            const patientName =
                patientInfo?.patient_name ||
                item.patient_name
                // (pId ? `Patient #${pId}` : '-');
            const isAttested = isPatientFullyAttested(pId);

            const rxAttested = Number(item.rx_attested) === 1 || item.rx_attested === true;
            const hcpConfirmed =
                Number(item.hcp_confirmed) === 1 || item.hcp_confirmed === true;

            const rawSource = (item.source || '').toLowerCase();
            const source = rawSource === 'interim' ? 'interim' : 'cycle';
            const sourceLabel = source === 'interim' ? 'New' : 'Cycle';

            return {
                id: item.id || item.cycle_detail_id,
                patientId: pId,
                source: source,
                sourceLabel: sourceLabel,
                patient: patientName,
                // externalPatientId: item.external_patient_id || pId || '-',
                patientStatus: isAttested,
                patientAttestedBy: item.attested_by || item.rx_attested_by_name || '-',
                patientAttestedOn: formatDate(item.attested_on || item.rx_attested_on),
                rx: item.external_prescription_id || item.pharmacy_order_id || '-',
                drug: item.drug || '-',
                rxAttested: rxAttested,
                rxAttestedBy:
                    item.rx_attested_by_name ||
                    item.rx_attested_by_username ||
                    (item.rx_attested_by ? String(item.rx_attested_by) : '-'),
                rxAttestedOn: formatDate(item.rx_attested_on),
                hcpConfirmed: hcpConfirmed,
                hcpConfirmedBy:
                    item.hcp_confirmed_by_name ||
                    item.hcp_confirmed_by_username ||
                    (item.hcp_confirmed_by ? String(item.hcp_confirmed_by) : '-'),
                hcpConfirmedOn: formatDate(item.hcp_confirmed_on)
            };
        });

        if (selectedSource) {
            resultRows = resultRows.filter(row => row.source === selectedSource);
        }

        if (selectedPatientId) {
            resultRows = resultRows.filter(
                row => String(row.patientId) === String(selectedPatientId)
            );
        }

        if (activeFilter === 'ATTESTED_PATIENTS') {
            resultRows = resultRows.filter(row => row.patientStatus === true);
        } else if (activeFilter === 'PENDING_PATIENTS') {
            resultRows = resultRows.filter(row => row.patientStatus !== true);
        } else if (activeFilter === 'PENDING_RX') {
            resultRows = resultRows.filter(row => row.rxAttested !== true);
        } else if (activeFilter === 'PENDING_HCP') {
            resultRows = resultRows.filter(row => row.hcpConfirmed !== true);
        }

        // Sort so that New (interim) source elements appear on top
        resultRows.sort((a, b) => {
            if (a.source === 'interim' && b.source !== 'interim') return -1;
            if (a.source !== 'interim' && b.source === 'interim') return 1;
            return 0;
        });

        return resultRows;
    }, [
        rawRows,
        patientMap,
        isPatientFullyAttested,
        selectedSource,
        selectedPatientId,
        activeFilter
    ]);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'sourceLabel',
                header: 'Source',
                meta: {
                    headerProps: { className: 'text-900 text-center px-2' },
                    cellProps: { className: 'text-center px-2' }
                },
                cell: ({ row: { original } }) => (
                    <span
                        className={`badge ${
                            original.source === 'interim'
                                ? 'bg-success'
                                : 'bg-secondary'
                        }`}
                    >
                        {original.sourceLabel}
                    </span>
                )
            },
            {
                accessorKey: 'patient',
                header: 'Patient',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            // {
            //     accessorKey: 'externalPatientId',
            //     header: 'Patient ID',
            //     meta: {
            //         headerProps: { className: 'text-900 text-start px-2' },
            //         cellProps: { className: 'text-start px-2' }
            //     }
            // },
            {
                accessorKey: 'rx',
                header: 'Rx',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'drug',
                header: 'Drug',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'rxAttested',
                header: 'Rx Attested',
                meta: {
                    headerProps: { className: 'text-900 text-center px-2' },
                    cellProps: { className: 'text-center px-2' }
                },
                cell: ({ row: { original } }) =>
                    original.rxAttested ? (
                        <FontAwesomeIcon icon="check" className="text-success" />
                    ) : (
                        '-'
                    )
            },
            {
                accessorKey: 'rxAttestedBy',
                header: 'Rx By',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'rxAttestedOn',
                header: 'Rx On',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'hcpConfirmed',
                header: 'HCP Confirmed',
                meta: {
                    headerProps: { className: 'text-900 text-center px-2' },
                    cellProps: { className: 'text-center px-2' }
                },
                cell: ({ row: { original } }) =>
                    original.hcpConfirmed ? (
                        <FontAwesomeIcon icon="check" className="text-success" />
                    ) : (
                        '-'
                    )
            },
            {
                accessorKey: 'hcpConfirmedBy',
                header: 'HCP By',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'hcpConfirmedOn',
                header: 'HCP On',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'patientStatus',
                header: 'Patient',
                meta: {
                    headerProps: { className: 'text-900 text-center px-2' },
                    cellProps: { className: 'text-center px-2' }
                },
                cell: ({ row: { original } }) =>
                    original.patientStatus ? (
                        <FontAwesomeIcon icon="check" className="text-success" />
                    ) : (
                        '-'
                    )
            }
        ],
        []
    );

    const table = useAdvanceTable({
        data: rows,
        columns,
        selection: false,
        sortable: true,
        pagination: true
    });

    return (
        <div className="mt-3">
            {loading && (
                <div className="d-flex align-items-center justify-content-center py-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span className="fs-10">Loading attestation details</span>
                </div>
            )}

            {!loading && (
                <>
                    <Row className="g-3 mb-3">
                        <Col md={2} sm={4} xs={6}>
                            <div
                                className={`border rounded-2 p-2 h-100 ${activeFilter === 'ALL' ? 'border-primary bg-100 shadow-sm' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveFilter('ALL')}
                            >
                                <div className="text-700 small">Total Patients</div>
                                <div className="fw-bold fs-6">{summary.total_patients || 0}</div>
                            </div>
                        </Col>
                        <Col md={2} sm={4} xs={6}>
                            <div
                                className={`border rounded-2 p-2 h-100 ${activeFilter === 'ATTESTED_PATIENTS' ? 'border-primary bg-100 shadow-sm' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveFilter('ATTESTED_PATIENTS')}
                            >
                                <div className="text-700 small">Attested Patients</div>
                                <div className="fw-bold fs-6">
                                    {summary.fully_attested_patients || 0}
                                </div>
                            </div>
                        </Col>
                        <Col md={2} sm={4} xs={6}>
                            <div
                                className={`border rounded-2 p-2 h-100 ${activeFilter === 'PENDING_PATIENTS' ? 'border-primary bg-100 shadow-sm' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveFilter('PENDING_PATIENTS')}
                            >
                                <div className="text-700 small">Attestation Pending Patients</div>
                                <div className="fw-bold fs-6">{summary.pending_patients || 0}</div>
                            </div>
                        </Col>
                        <Col md={2} sm={4} xs={6}>
                            <div
                                className={`border rounded-2 p-2 h-100 ${activeFilter === 'ALL_PRESCRIPTIONS' ? 'border-primary bg-100 shadow-sm' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveFilter('ALL_PRESCRIPTIONS')}
                            >
                                <div className="text-700 small">Total Prescriptions</div>
                                <div className="fw-bold fs-6">{summary.total_prescriptions || 0}</div>
                            </div>
                        </Col>
                        <Col md={2} sm={4} xs={6}>
                            <div
                                className={`border rounded-2 p-2 h-100 ${activeFilter === 'PENDING_RX' ? 'border-primary bg-100 shadow-sm' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveFilter('PENDING_RX')}
                            >
                                <div className="text-700 small">Medication Accuracy Check Pending</div>
                                <div className="fw-bold fs-6">
                                    {summary.pending_rx_attestation || 0}
                                </div>
                            </div>
                        </Col>
                        <Col md={2} sm={4} xs={6}>
                            <div
                                className={`border rounded-2 p-2 h-100 ${activeFilter === 'PENDING_HCP' ? 'border-primary bg-100 shadow-sm' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveFilter('PENDING_HCP')}
                            >
                                <div className="text-700 small">HCP Confirmation Pending</div>
                                <div className="fw-bold fs-6">
                                    {summary.pending_hcp_confirmation || 0}
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <AdvanceTableProvider {...table}>
                        <Row className="align-items-end justify-content-between mb-3">
                            <Col md={4} className="mb-2 mb-md-0">
                                <AdvanceTableSearchBox placeholder="Search..." />
                            </Col>
                            <Col md={4} className="mb-2 mb-md-0">
                                <Form.Group>
                                    <Form.Label className="mb-1">Source</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={selectedSource}
                                        onChange={event => setSelectedSource(event.target.value)}
                                    >
                                        <option value="">All Sources</option>
                                        <option value="cycle">Cycle</option>
                                        <option value="interim">New</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="mb-1">Patient</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={selectedPatientId}
                                        onChange={event => setSelectedPatientId(event.target.value)}
                                    >
                                        <option value="">All Patients</option>
                                        {patients.map(patient => {
                                            const name =
                                                patient.patient_name ||
                                                `${patient.patient_firstname || ''} ${patient.patient_lastname || ''}`.trim() ||
                                                // patient.external_patient_id ||
                                                patient.patient_id;

                                            return (
                                                <option
                                                    key={patient.patient_id}
                                                    value={patient.patient_id}
                                                >
                                                    {name}
                                                </option>
                                            );
                                        })}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        {rows.length > 0 ? (
                            <>
                                <Row>
                                    <Col xs="12" className="mb-1 mt-1">
                                        <AdvanceTable
                                            headerClassName="bg-200 text-nowrap align-middle text-center"
                                            rowClassName="text-nowrap align-middle text-center"
                                            tableProps={{
                                                size: 'sm',
                                                striped: true,
                                                className: 'fs-10 mb-0 overflow-hidden'
                                            }}
                                        />
                                    </Col>
                                </Row>
                                <Card.Footer>
                                    <div className="mt-3">
                                        <AdvanceTableFooter
                                            rowInfo
                                            navButtons
                                            rowsPerPageSelection
                                        />
                                    </div>
                                </Card.Footer>
                            </>
                        ) : (
                            <div className="text-700 small py-3">No attestation records found</div>
                        )}
                    </AdvanceTableProvider>
                </>
            )}
        </div>
    );
};

CycleAttestation.propTypes = {
    attestations: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    loading: PropTypes.bool
};

export default CycleAttestation;
